
import { Apis } from '@/appSettings'
import { useAuth } from '@/context/AuthContext'
import { usePrintContext } from '@/context/PrintContext'
import { useOffersContext } from '@/context/useOffersContext'
import { useUrl } from '@/hooks'
import { useGetOfferDocuments } from '@/hooks/useDocuments'
import type { Document } from 'BrokerTsLib/src/dtos'

import { Menu, MenuButton, MenuList, Stack, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { formatDateTime } from 'BrokerTsLib/src/Utils'
import { useEffect, useMemo } from 'react'
import { Loading } from '../Loading'


export const OfferDocuments = ({ offerId }: { offerId: number }) => {
  const { user } = useAuth()
  const { translate } = useTranslationsContext()
  const { printMode } = usePrintContext()

  const { calculatingOffersIds } = useOffersContext()
  const isRecalculating = calculatingOffersIds?.includes(offerId) ?? false

  const documentUrl = useUrl(Apis.document)

  const {
    refetch: getOfferDocuments,
    data: offerDocuments,
    isFetching
  } = useGetOfferDocuments(offerId, { enabled: false, retry: false })

  const xmlDocs = offerDocuments?.data.filter(
    (x) => x.name !== 'OfferDocument',
  ) ?? []

  // doctu nove dokumenty po kazde rekalkulaci
  useEffect(() => {
    if (!isRecalculating && xmlDocs && xmlDocs.length > 0) {
      getOfferDocuments()
    }
  }, [isRecalculating])

  const { offerAndCalcPairs, contractPairs, others } = useMemo(() => {
    return processDocuments(offerDocuments?.data ?? [])
  }, [offerDocuments])

  if (!user?.enableOfferXml || printMode)
    return null

  const tableHeaderStyle = { px: 2 }
  // const tableHeaderStyle = { textAlign: 'left' as const, padding: '8px', borderBottom: '2px solid #E2E8F0' }
  const cellStyle = { p: 2 }
  // const cellStyle = { padding: '8px', borderBottom: '1px solid #EDF2F7' }

  return (
    <Menu size="sm" placement="bottom">
      <MenuButton
        sx={{ gap: 2, whiteSpace: 'nowrap' }}
        type="button"
        onClick={() => {
          if (!xmlDocs || xmlDocs.length == 0)
            getOfferDocuments()
        }}
        disabled={isRecalculating || isFetching}
      >
        {translate('xml', 'XML').toUpperCase()}
      </MenuButton>
      <MenuList>
        {isFetching
          ? <Loading my={4} />
          : <Stack gap={4} p={4}>
            {offerAndCalcPairs.length > 0 &&
                            <Stack>
                              {/* TABULKA 1: PÁROVANÉ REQUESTY A RESPONSY */}
                              <h6><strong>{translate('calculation', 'Kalkulace')}</strong></h6>
                              <Table size="sm" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th {...tableHeaderStyle}>{translate('date', 'Datum')}</Th>
                                    <Th {...tableHeaderStyle}>Request</Th>
                                    <Th {...tableHeaderStyle}>Response</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {offerAndCalcPairs.map((pair) => (
                                    <Tr key={pair.input.id}>
                                      <Td {...cellStyle}>{formatDateTime(pair.input.date, 'd.M.yyyy HH:mm:ss.SSS')}</Td>
                                      <Td {...cellStyle}>
                                        <a href={`${documentUrl}/GetOfferDocument?docId=${pair.input.id}`} target="_blank" style={{ color: '#3182ce', fontWeight: 'bold' }}>
                                          {pair.input.docType?.toUpperCase() ?? pair.input.name} {user.adminCebiaRights && `(${pair.input.id})`}
                                        </a>
                                      </Td>
                                      <Td {...cellStyle}>
                                        <a href={`${documentUrl}/GetOfferDocument?docId=${pair.output.id}`} target="_blank" style={{ color: '#3182ce', fontWeight: 'bold' }}>
                                          {pair.output.docType?.toUpperCase() ?? pair.output.name} {user.adminCebiaRights && `(${pair.output.id})`}
                                        </a>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Stack>
            }

            {contractPairs.length > 0 &&
                            <Stack>
                              {/* TABULKA 2: PÁROVANÉ REQUESTY A RESPONSY SJEDNANI */}
                              <h6><strong>{translate('createContract', 'Sjednání smlouvy')}</strong></h6>
                              <Table size="sm" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th {...tableHeaderStyle}>{translate('date', 'Datum')}</Th>
                                    <Th {...tableHeaderStyle}>Request</Th>
                                    <Th {...tableHeaderStyle}>Response</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {contractPairs.map((pair) => (
                                    <Tr key={pair.input.id}>
                                      <Td {...cellStyle}>{formatDateTime(pair.input.date, 'd.M.yyyy HH:mm:ss.SSS')}</Td>
                                      <Td {...cellStyle}>
                                        <a href={`${documentUrl}/GetOfferDocument?docId=${pair.input.id}`} target="_blank" style={{ color: '#3182ce', fontWeight: 'bold' }}>
                                          {pair.input.docType?.toUpperCase() ?? pair.input.name} {user.adminCebiaRights && `(${pair.input.id})`}
                                        </a>
                                      </Td>
                                      <Td {...cellStyle}>
                                        <a href={`${documentUrl}/GetOfferDocument?docId=${pair.output.id}`} target="_blank" style={{ color: '#3182ce', fontWeight: 'bold' }}>
                                          {pair.output.docType?.toUpperCase() ?? pair.output.name} {user.adminCebiaRights && `(${pair.output.id})`}
                                        </a>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Stack>
            }

            {/* TABULKA 3: VŠE OSTATNÍ */}
            {others.length > 0 &&
                            <Stack>
                              <h6><strong>{translate('other', 'Ostatní')}</strong></h6>
                              <Table size="sm" variant="simple">
                                <Thead>
                                  <Tr>
                                    <Th {...tableHeaderStyle}>{translate('date', 'Datum')}</Th>
                                    <Th {...tableHeaderStyle}>{translate('name', 'Název')}</Th>
                                    <Th {...tableHeaderStyle}>{translate('type', 'Typ')}</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {others.map((doc) => (
                                    <Tr key={doc.id}>
                                      <Td {...cellStyle}>{formatDateTime(doc.date, 'd.M.yyyy HH:mm:ss.SSS')}</Td>
                                      <Td {...cellStyle}>{doc.name}</Td>
                                      <Td {...cellStyle}>
                                        <a href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`} target="_blank" style={{ color: '#3182ce', fontWeight: 'bold' }}>
                                          {doc.docType?.toUpperCase() ?? doc.name} {user.adminCebiaRights && `(${doc.id})`}
                                        </a>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Stack>
            }
          </Stack>
        }
      </MenuList>
    </Menu>
  )
}

const processDocuments = (docs: Document[]) => {
  const remaining = [...docs]

  const extractPairs = (inputName: string, outputName: string) => {
    const pairs: {
      input: Document
      output: Document
      date: Date
    }[] = []

    // Najdeme všechny inputy daného typu
    const inputs = remaining.filter(d => d.name === inputName)

    inputs.forEach(input => {
      const inputTime = new Date(input.date).getTime()

      // Hledáme output, který má stejný název a časový rozdíl je menší než 1000ms
      const outputIdx = remaining.findIndex(d =>
        d.name === outputName &&
                Math.abs(new Date(d.date).getTime() - inputTime) < 1000
      )

      if (outputIdx !== -1) {
        const output = remaining[outputIdx]
        pairs.push({
          input,
          output,
          date: new Date(input.date)
        })

        // Odstraníme oba z "remaining" pole
        const inputIdxInRem = remaining.findIndex(d => d.id === input.id)
        if (inputIdxInRem !== -1) remaining.splice(inputIdxInRem, 1)

        const outputIdxInRem = remaining.findIndex(d => d.id === output.id)
        if (outputIdxInRem !== -1) remaining.splice(outputIdxInRem, 1)
      }
    })

    return pairs.sort((a, b) => b.date.getTime() - a.date.getTime())
  }

  // 1. Tabulka: kalkulace
  const offerAndCalcPairs = [
    ...extractPairs('nabidka_xml_input', 'nabidka_xml_output'),
    ...extractPairs('Calculation_XmlInput', 'Calculation_XmlOutput')
  ].sort((a, b) => b.date.getTime() - a.date.getTime())

  // 2. Tabulka: smlouvy
  const contractPairs = extractPairs('contract_xml_input', 'contract_xml_output')

  // 3. Tabulka: ostatní 
  const others = remaining.sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  )

  return { offerAndCalcPairs, contractPairs, others }
}